import React, { useState, useEffect, useRef, useCallback } from 'react';
import mqtt from 'mqtt';
import axios from 'axios';
import './Page15.css';

const BROKER = 'wss://kidr78home.duckdns.org/mqtt';
const API = 'https://kidr78home.duckdns.org/api/lumen';
const AUTH = 'kSuIlUmNaTiIeO5';
const hdrs = { headers: { Authorization: AUTH } };

function Page16() {
  const [status, setStatus] = useState('Connecting...');
  const [cogniState, setCogniState] = useState(null);
  const [emotions, setEmotions] = useState(null);
  const [resonance, setResonance] = useState(null);
  const [activity, setActivity] = useState(null);
  const [surprise, setSurprise] = useState(null);
  const [monologue, setMonologue] = useState([]);
  const [feed, setFeed] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [waitingReply, setWaitingReply] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [attention, setAttention] = useState(null);
  const [landscape, setLandscape] = useState(null);
  const [worldview, setWorldview] = useState(null);
  const [intents, setIntents] = useState(null);
  const [temporal, setTemporal] = useState(null);
  const [goals, setGoals] = useState(null);
  const [intuition, setIntuition] = useState(null);
  const [expandedWv, setExpandedWv] = useState(null);

  const clientRef = useRef(null);
  const feedEndRef = useRef(null);
  const maxFeed = 100;

  const addFeedItem = useCallback((item) => {
    setFeed(prev => [...prev.slice(-(maxFeed - 1)), { ...item, id: Date.now() + Math.random() }]);
  }, []);

  // Load historical data
  useEffect(() => {
    const load = async () => {
      try {
        const results = await Promise.allSettled([
          axios.get(`${API}/feed?limit=50`, hdrs),
          axios.get(`${API}/emotions`, hdrs),
          axios.get(`${API}/resonance`, hdrs),
          axios.get(`${API}/attention`, hdrs),
          axios.get(`${API}/landscape`, hdrs),
          axios.get(`${API}/worldview`, hdrs),
          axios.get(`${API}/intents`, hdrs),
          axios.get(`${API}/temporal`, hdrs),
          axios.get(`${API}/goals`, hdrs),
          axios.get(`${API}/intuition`, hdrs),
        ]);
        const [feedRes, emoRes, resRes, attRes, landRes, wvRes, intRes, tempRes, goalsRes, intuitionRes] = results;

        if (emoRes.status === 'fulfilled' && emoRes.value.data.engagement !== undefined) setEmotions(emoRes.value.data);
        if (resRes.status === 'fulfilled' && resRes.value.data.safety !== undefined) setResonance(resRes.value.data);
        if (attRes.status === 'fulfilled' && attRes.value.data.cloud) setAttention(attRes.value.data);
        if (landRes.status === 'fulfilled' && landRes.value.data.total_active !== undefined) setLandscape(landRes.value.data);
        if (wvRes.status === 'fulfilled' && wvRes.value.data.worldview) setWorldview(wvRes.value.data);
        if (intRes.status === 'fulfilled' && intRes.value.data.intents) setIntents(intRes.value.data.intents);
        if (tempRes.status === 'fulfilled' && tempRes.value.data.patterns) setTemporal(tempRes.value.data.patterns);
        if (goalsRes.status === 'fulfilled' && goalsRes.value.data.goals) setGoals(goalsRes.value.data.goals);
        if (intuitionRes.status === 'fulfilled' && intuitionRes.value.data.felt_sense !== undefined) setIntuition(intuitionRes.value.data);

        if (feedRes.status === 'fulfilled' && feedRes.value.data.items) {
          const items = feedRes.value.data.items.map(item => {
            const payload = item.payload || {};
            let type = 'status', data = {};
            if (item.subtopic === 'thoughts') { type = 'thought'; data = { type: payload.type || item.msg_type, message: item.message || payload.message, timestamp: item.ts }; }
            else if (item.subtopic === 'dreams') { type = 'dream'; data = { narrative: item.narrative || payload.narrative, insights: payload.insights, message: payload.message, questions: payload.questions, cost: payload.cost, timestamp: item.ts }; }
            else if (item.subtopic === 'status') { type = 'status'; data = { state: payload.state, thought: payload.thought || item.message, event: payload.event, timestamp: item.ts }; if (payload.state) setCogniState(payload); }
            else if (item.subtopic === 'response') { type = 'response'; data = { question: payload.question, answer: payload.answer || item.message, timestamp: item.ts }; }
            if (item.emotions) data.emotions = item.emotions;
            return { type, data, id: `hist-${item.ts}-${Math.random()}` };
          });
          setFeed(items);
        }
      } catch (err) { console.error('Failed to load Lumen history:', err); }
      finally { setLoadingHistory(false); }
    };
    load();
  }, []);

  // MQTT connection — subscribe to blank/# (Lumen's topic prefix)
  useEffect(() => {
    const client = mqtt.connect(BROKER);
    clientRef.current = client;
    client.on('connect', () => { setStatus('Connected'); client.subscribe('blank/#'); });
    client.on('error', () => setStatus('Connection Error'));
    client.on('close', () => setStatus('Disconnected'));

    client.on('message', (topic, msg) => {
      try {
        const data = JSON.parse(msg.toString());
        const sub = topic.replace('blank/', '');
        if (sub === 'status') { setCogniState(data); if (data.emotions) setEmotions(data.emotions); addFeedItem({ type: 'status', data }); }
        else if (sub === 'emotions') setEmotions(data);
        else if (sub === 'resonance') setResonance(data);
        else if (sub === 'activity') { setActivity(data); if (data.meta?.emotions) setEmotions(data.meta.emotions); }
        else if (sub === 'subconscious') setSurprise(data);
        else if (sub === 'monologue') setMonologue(prev => [...prev.slice(-9), data]);
        else if (sub === 'thoughts') { if (data.emotions) setEmotions(data.emotions); addFeedItem({ type: 'thought', data }); }
        else if (sub === 'dreams') { if (data.emotions) setEmotions(data.emotions); addFeedItem({ type: 'dream', data }); }
        else if (sub === 'response') { if (data.emotions) setEmotions(data.emotions); addFeedItem({ type: 'response', data }); setWaitingReply(false); }
      } catch (e) { console.error('Lumen MQTT parse error:', e); }
    });
    return () => client.end();
  }, [addFeedItem]);

  const prevChatCount = useRef(0);
  useEffect(() => {
    const chatItems = feed.filter(i => i.type === 'user' || i.type === 'response');
    if (chatItems.length > prevChatCount.current) {
      feedEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    prevChatCount.current = chatItems.length;
  }, [feed]);

  const sendMessage = () => {
    const text = chatInput.trim();
    if (!text || !clientRef.current) return;
    clientRef.current.publish('blank/inbox', text);
    addFeedItem({ type: 'user', data: { message: text, timestamp: new Date().toISOString() } });
    setChatInput('');
    setWaitingReply(true);
  };

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const [gRes, iRes] = await Promise.allSettled([
          axios.get(`${API}/goals`, hdrs),
          axios.get(`${API}/intuition`, hdrs),
        ]);
        if (gRes.status === 'fulfilled' && gRes.value.data.goals) setGoals(gRes.value.data.goals);
        if (iRes.status === 'fulfilled' && iRes.value.data.felt_sense !== undefined) setIntuition(iRes.value.data);
      } catch {}
    }, 120000);
    return () => clearInterval(interval);
  }, []);

  const fmt = (ts) => { if (!ts) return ''; try { return new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };
  const fmtDate = (ts) => { if (!ts) return ''; try { const d = new Date(ts); return `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} ${fmt(ts)}`; } catch { return ''; } };

  const emoBar = (label, value, color) => (
    <div className="emo-row" key={label}>
      <span className="emo-label">{label}</span>
      <div className="emo-track"><div className="emo-fill" style={{ width: `${(value || 0) * 100}%`, background: color }} /></div>
      <span className="emo-val">{((value || 0) * 100).toFixed(0)}%</span>
    </div>
  );

  const resBar = (label, value, negLabel, posLabel) => {
    const v = value || 0;
    const pct = ((v + 1) / 2) * 100;
    const color = v >= 0 ? `rgba(46, 204, 113, ${0.4 + v * 0.6})` : `rgba(231, 76, 60, ${0.4 + Math.abs(v) * 0.6})`;
    return (
      <div className="res-row" key={label}>
        <span className="res-neg">{negLabel}</span>
        <div className="res-track"><div className="res-center" /><div className="res-fill" style={{ left: Math.min(pct, 50) + '%', width: Math.abs(pct - 50) + '%', background: color }} /></div>
        <span className="res-pos">{posLabel}</span>
      </div>
    );
  };

  const surpriseLevel = (v) => v < 0.2 ? 'expected' : v < 0.5 ? 'curious' : v < 0.75 ? 'surprised' : 'very surprised';
  const surpriseColor = (v) => v < 0.2 ? '#2ecc71' : v < 0.5 ? '#f39c12' : v < 0.75 ? '#e67e22' : '#e74c3c';
  const actIcon = (s) => ({ resting: '😴', pulsing: '💓', processing_inbox: '📬', thinking: '🧠', idle: '✅', dreaming: '🌙', investigating: '🔍', running_task: '⚙️' }[s] || '⚡');
  const actColor = (s) => ({ resting: '#9b59b6', pulsing: '#e74c3c', processing_inbox: '#f39c12', thinking: '#3498db', idle: '#2ecc71', dreaming: '#9b59b6', investigating: '#e67e22', running_task: '#1abc9c' }[s] || '#95a5a6');
  const stateIcon = (s) => ({ resting: '😴', processing: '⚡', idle: '💭', offline: '🔌' }[s] || '❓');
  const stabilityLabel = (v) => v < 0.3 ? 'forming' : v < 0.7 ? 'developing' : 'deeply held';

  const renderRichText = (text) => {
    if (!text) return null;
    const segments = text.split(/(```[\s\S]*?```)/g);
    return segments.map((segment, si) => {
      if (segment.startsWith('```') && segment.endsWith('```')) {
        const inner = segment.slice(3, -3);
        const nl = inner.indexOf('\n');
        const code = nl >= 0 ? inner.slice(nl + 1) : inner;
        return <pre className="rich-code-block" key={si}><code>{code.trim()}</code></pre>;
      }
      return segment.split(/\n\n+/).map((para, pi) => {
        const key = `${si}-${pi}`;
        const lines = para.split('\n');

        // Detect markdown tables
        const tableLines = lines.filter(l => l.trim().startsWith('|') && l.trim().endsWith('|'));
        if (tableLines.length >= 2) {
          const dataRows = tableLines.filter(l => !/^\|[\s\-:]+\|$/.test(l.trim()));
          if (dataRows.length >= 1) {
            const parseRow = (row) => row.split('|').filter((_, i, arr) => i > 0 && i < arr.length - 1).map(c => c.trim());
            const headers = parseRow(dataRows[0]);
            const bodyRows = dataRows.slice(1).map(parseRow);
            return (
              <table className="rich-table" key={key}>
                <thead><tr>{headers.map((h, i) => <th key={i}>{renderInline(h)}</th>)}</tr></thead>
                <tbody>{bodyRows.map((row, ri) => <tr key={ri}>{row.map((cell, ci) => <td key={ci}>{renderInline(cell)}</td>)}</tr>)}</tbody>
              </table>
            );
          }
        }

        // Detect inline pipe tables
        const pipeLines = lines.filter(l => (l.match(/\|/g) || []).length >= 3);
        if (pipeLines.length >= 2 && pipeLines.length === lines.filter(l => l.trim()).length) {
          const dataRows = pipeLines.filter(l => !/^[\s\-:|]+$/.test(l.trim()));
          if (dataRows.length >= 1) {
            const parseRow = (row) => row.split('|').map(c => c.trim()).filter(c => c);
            const headers = parseRow(dataRows[0]);
            const bodyRows = dataRows.slice(1).map(parseRow);
            return (
              <table className="rich-table" key={key}>
                <thead><tr>{headers.map((h, i) => <th key={i}>{renderInline(h)}</th>)}</tr></thead>
                <tbody>{bodyRows.map((row, ri) => <tr key={ri}>{row.map((cell, ci) => <td key={ci}>{renderInline(cell)}</td>)}</tr>)}</tbody>
              </table>
            );
          }
        }

        const isList = lines.every(l => /^\s*[\*\-•]\s/.test(l) || !l.trim());
        if (isList) {
          return <ul className="rich-list" key={key}>{lines.filter(l => l.trim()).map((li, i) => <li key={i}>{renderInline(li.replace(/^\s*[\*\-•]\s*/, ''))}</li>)}</ul>;
        }
        return <p className="rich-para" key={key}>{renderInline(para)}</p>;
      });
    });
  };

  const renderInline = (text) => {
    if (!text) return null;
    return text.split(/(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*)/g).map((p, i) => {
      if (p.startsWith('`') && p.endsWith('`') && p.length > 1) return <code className="rich-inline-code" key={i}>{p.slice(1, -1)}</code>;
      if (p.startsWith('**') && p.endsWith('**') && p.length > 4) return <strong key={i}>{p.slice(2, -2)}</strong>;
      if (p.startsWith('*') && p.endsWith('*') && p.length > 2) return <em key={i}>{p.slice(1, -1)}</em>;
      return p;
    });
  };

  const renderFeedItem = (item) => {
    switch (item.type) {
      case 'thought': {
        const isTaskResult = item.data.type === 'task_result';
        return (<div className={`feed-card thought ${isTaskResult ? 'task-result' : ''}`} key={item.id}><div className="feed-badge">{isTaskResult ? '⚙️ Task Result' : '💭'} {!isTaskResult && (item.data.type === 'dream_message' ? 'Dream thought' : 'Thought')}</div><div className="feed-text rich-content">{renderRichText(item.data.message)}</div><span className="feed-time">{fmt(item.data.timestamp)}</span></div>);
      }
      case 'dream':
        return (<div className="feed-card dream" key={item.id}><div className="feed-badge">🌙 Dream</div><div className="feed-text rich-content">{renderRichText(item.data.narrative)}</div>
          {item.data.insights?.length > 0 && <div className="dream-insights">{item.data.insights.map((ins, i) => <span key={i} className="insight-tag">💡 {ins}</span>)}</div>}
          {item.data.message && <p className="dream-msg">📨 "{item.data.message}"</p>}
          <span className="feed-time">{fmt(item.data.timestamp)}</span></div>);
      case 'status':
        return (<div className={`feed-card status-card ${isTaskStatus(item) ? 'task-status' : ''}`} key={item.id}><div className="feed-badge">{stateIcon(item.data.state)} {item.data.state}{item.data.event ? ` · ${item.data.event}` : ''}</div>{item.data.thought && <p className="feed-text">{item.data.thought}</p>}<span className="feed-time">{fmt(item.data.timestamp)}</span></div>);
      case 'user':
        return (<div className="feed-card user-msg" key={item.id}><div className="feed-badge">🧑 You</div><p className="feed-text">{item.data.message}</p><span className="feed-time">{fmt(item.data.timestamp)}</span></div>);
      case 'response':
        return (<div className="feed-card cogni-reply" key={item.id}><div className="feed-badge">🤖 Lumen</div>{item.data.question && <p className="feed-question">Re: "{item.data.question}"</p>}<div className="feed-text rich-content">{renderRichText(item.data.answer)}</div><span className="feed-time">{fmt(item.data.timestamp)}</span></div>);
      default: return null;
    }
  };

  // Helpers to identify task-related feed items
  const isTaskStatus = (item) => {
    if (item.type !== 'status') return false;
    const s = (item.data.state || '').toLowerCase();
    const e = (item.data.event || '').toLowerCase();
    return s.includes('task') || e.includes('task') || s === 'investigating' || s === 'running_task';
  };

  const isTaskItem = (item) => {
    if (item.type === 'thought' && item.data.type === 'task_result') return true;
    if (isTaskStatus(item)) return true;
    return false;
  };

  const temporalGrid = () => {
    if (!temporal?.length) return null;
    const topTags = [...new Set(temporal.map(p => p.tag))].slice(0, 8);
    const grid = {};
    topTags.forEach(tag => { grid[tag] = new Array(24).fill(0); });
    temporal.forEach(p => { if (grid[p.tag]) grid[p.tag][p.hour] = (grid[p.tag][p.hour] || 0) + p.weight; });
    const maxW = Math.max(...Object.values(grid).flat(), 0.01);
    return { topTags, grid, maxW };
  };

  const attentionWords = () => {
    if (!attention?.cloud) return [];
    return Object.entries(attention.cloud).sort((a, b) => b[1] - a[1]).slice(0, 30);
  };

  const tg = temporalGrid();
  const aw = attentionWords();

  return (
    <div className="cogni-page">
      <div className="cogni-header">
        <h1 className="cogni-title">🏄 Lumen</h1>
        <div className="cogni-status-row">
          <span className={`conn-badge ${status === 'Connected' ? 'on' : ''}`}><span className="conn-dot" /> {status}</span>
          {cogniState && <span className="state-badge">{stateIcon(cogniState.state)} {cogniState.state}</span>}
        </div>
      </div>

      <div className="cogni-grid">
        <div className="cogni-top-row">
          <div className="panel mood-panel">
            <h3 className="panel-title">Mood</h3>
            {emotions ? <>
              {emoBar('Engagement', emotions.engagement, '#3498db')}
              {emoBar('Confidence', emotions.confidence, '#2ecc71')}
              {emoBar('Frustration', emotions.frustration, '#e74c3c')}
              {emoBar('Curiosity', emotions.curiosity, '#f39c12')}
              {emoBar('Fatigue', emotions.fatigue, '#9b59b6')}
              {emoBar('Boredom', emotions.boredom, '#d68910')}
              {emoBar('Anger', emotions.anger, '#c0392b')}
              {emoBar('Unhappy', emotions.unhappy, '#8e44ad')}
            </> : <p className="panel-waiting">Waiting for data...</p>}
          </div>

          <div className="panel resonance-panel">
            <h3 className="panel-title">Resonance</h3>
            {resonance ? <>
              {resBar('safety', resonance.safety, 'uneasy', 'secure')}
              {resBar('energy', resonance.energy, 'drained', 'energized')}
              {resBar('connection', resonance.connection, 'isolated', 'bonded')}
              {resBar('novelty', resonance.novelty, 'routine', 'discovering')}
              {resBar('tension', resonance.tension, 'calm', 'urgent')}
            </> : <p className="panel-waiting">Waiting for data...</p>}
            <div className="surprise-section">
              <h4 className="panel-subtitle">Surprise</h4>
              {surprise ? <>
                <div className="surprise-gauge"><div className="surprise-arc"><div className="surprise-fill" style={{ width: `${(surprise.surprise || 0) * 100}%`, background: surpriseColor(surprise.surprise || 0) }} /></div>
                <span className="surprise-label" style={{ color: surpriseColor(surprise.surprise || 0) }}>{surpriseLevel(surprise.surprise || 0)}</span></div>
              </> : <span className="panel-dim">No surprises</span>}
            </div>
          </div>

          <div className="panel attention-panel">
            <h3 className="panel-title">Attention Cloud</h3>
            {aw.length > 0 ? (
              <div className="word-cloud">
                {aw.map(([word, score], i) => {
                  const size = 0.7 + (score / (aw[0]?.[1] || 1)) * 0.9;
                  const opacity = 0.4 + (score / (aw[0]?.[1] || 1)) * 0.6;
                  return <span key={i} className="cloud-word" style={{ fontSize: `${size}rem`, opacity }}>{word}</span>;
                })}
              </div>
            ) : <p className="panel-waiting">Waiting for data...</p>}
          </div>
        </div>

        <div className="cogni-mid-row">
          <div className="panel intuition-panel">
            <h3 className="panel-title">🫀 Gut Feeling</h3>
            {intuition && intuition.felt_sense ? <>
              <p className="intuition-sense">"{intuition.felt_sense}"</p>
              <div className="intuition-meta">
                <div className="intuition-urgency">
                  <span className="intuition-label">Urgency</span>
                  <div className="emo-track"><div className="emo-fill" style={{ width: `${(intuition.urgency || 0) * 100}%`, background: intuition.urgency > 0.7 ? '#e74c3c' : intuition.urgency > 0.4 ? '#f39c12' : '#2ecc71' }} /></div>
                  <span className="emo-val">{((intuition.urgency || 0) * 100).toFixed(0)}%</span>
                </div>
                {intuition.pull_toward && <p className="intuition-pull">→ Drawn to: {intuition.pull_toward}</p>}
                {intuition.unease_about && <p className="intuition-unease">⚠ Uneasy about: {intuition.unease_about}</p>}
              </div>
            </> : <p className="panel-waiting">Waiting for gut feeling...</p>}
          </div>

          <div className="panel goals-panel">
            <h3 className="panel-title">🎯 Goals</h3>
            {goals && goals.length > 0 ? (
              <div className="goals-list">
                {goals.filter(g => g.status === 'active').map(g => (
                  <div key={g.id} className="goal-item active">
                    <div className="goal-header"><span className="goal-topic">{g.topic}</span><span className="goal-sessions">{g.session_count} sessions</span></div>
                    <div className="goal-bars">
                      <div className="goal-bar-row"><span className="goal-bar-label">Investment</span><div className="emo-track"><div className="emo-fill" style={{ width: `${(g.emotional_investment || 0) * 100}%`, background: '#bb86fc' }} /></div></div>
                      <div className="goal-bar-row"><span className="goal-bar-label">Priority</span><div className="emo-track"><div className="emo-fill" style={{ width: `${(g.priority || 0) * 100}%`, background: '#f39c12' }} /></div></div>
                    </div>
                    <span className="goal-meta">src: {g.source} · last: {g.last_worked_on ? fmtDate(g.last_worked_on) : 'never'}</span>
                  </div>
                ))}
              </div>
            ) : <p className="panel-waiting">No goals yet</p>}
          </div>
        </div>

        {/* Task Outputs — dedicated panel for task results (full width, prominent) */}
        <div className="panel task-outputs-panel">
          <h3 className="panel-title">⚙️ Task Outputs</h3>
          <div className="feed-container task-outputs-feed">
            {feed.filter(isTaskItem).length === 0 ? (
              <div className="feed-empty"><span>⚙️</span><p>No task outputs yet</p></div>
            ) : feed.filter(isTaskItem).slice().reverse().map(renderFeedItem)}
          </div>
        </div>

        {/* Inner Life — thoughts, dreams, status (excluding task items) */}
        <div className="panel inner-life-panel">
          <h3 className="panel-title">🌊 Inner Life</h3>
          <div className="feed-container inner-life-feed">
            {feed.filter(i => (i.type === 'thought' || i.type === 'dream' || i.type === 'status') && !isTaskItem(i)).length === 0 ? (
              <div className="feed-empty"><span>🏄</span><p>Waiting for Lumen's thoughts...</p></div>
            ) : feed.filter(i => (i.type === 'thought' || i.type === 'dream' || i.type === 'status') && !isTaskItem(i)).slice().reverse().map(renderFeedItem)}
          </div>
        </div>

        {/* Chat — direct conversation (always last) */}
        <div className="panel chat-panel">
          <h3 className="panel-title">💬 Chat</h3>
          <div className="feed-container chat-feed">
            {loadingHistory ? (
              <div className="feed-empty"><span>⏳</span><p>Loading Lumen's history...</p></div>
            ) : feed.filter(i => i.type === 'user' || i.type === 'response').length === 0 ? (
              <div className="feed-empty"><span>💬</span><p>Send a message to start chatting</p></div>
            ) : feed.filter(i => i.type === 'user' || i.type === 'response').map(renderFeedItem)}
            {waitingReply && <div className="feed-card cogni-typing"><span className="typing-dots">🤖 Lumen is thinking<span className="dots">...</span></span></div>}
            <div ref={feedEndRef} />
          </div>

          <div className={`activity-strip ${activity ? activity.state : 'idle'}`}>
            <span className="activity-icon" style={{ color: actColor(activity ? activity.state : 'idle') }}>{actIcon(activity ? activity.state : 'idle')}</span>
            <span className="activity-detail">{activity ? activity.detail : 'Waiting for activity...'}</span>
          </div>

          <div className="chat-bar">
            <textarea className="chat-input" placeholder="Message Lumen... (Shift+Enter for new line)" value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              disabled={waitingReply} rows={1}
              onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; }}
            />
            <button className="chat-send" onClick={sendMessage} disabled={!chatInput.trim() || waitingReply} aria-label="Send message">📨</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Page16;
